import {
  DietMealProps,
  CookbookMealProps,
  FavMealProps,
  TrackerMealProps,
  TrackerAddMealProps,
  DescribeMealProps,
  Ingredient,
  IngredientMeasurement,
  DishIngredient,
  UnifiedMealData,
} from '../types';
import { getBaseMeasurement } from './unitConversion';
import { scaleIngredientMacro } from './mealCalculations';

/**
 * Combine the measurements of two occurrences of the same product.
 *
 * Amounts add up in the base unit; the household measure (piece, slice, …) is rescaled to that sum
 * so it keeps describing the whole ingredient, and the base measurement stays last — that is the
 * slot the tracker form edits. Summing per unit instead would leave a household measure that covers
 * only one occurrence, and could push it into the edited slot.
 */
const mergeMeasurements = (
  existing: IngredientMeasurement[],
  incoming: IngredientMeasurement[]
): IngredientMeasurement[] => {
  const existingBase = getBaseMeasurement(existing);
  const incomingBase = getBaseMeasurement(incoming);

  // Nothing sensible to add up (no measurements, or the API measures the occurrences in different
  // units) — keep what we already have rather than inventing a total.
  if (!existingBase || !incomingBase || existingBase.unit !== incomingBase.unit) {
    return existing;
  }

  const totalAmount = existingBase.amount + incomingBase.amount;
  const householdOf = (measurements: IngredientMeasurement[], base: IngredientMeasurement) =>
    measurements.find((measurement) => measurement !== base);
  const existingHousehold = householdOf(existing, existingBase);
  const incomingHousehold = householdOf(incoming, incomingBase);

  const scaled = (household: IngredientMeasurement, base: IngredientMeasurement) => ({
    ...household,
    amount: base.amount > 0 ? (household.amount * totalAmount) / base.amount : household.amount,
  });

  const household = existingHousehold
    ? scaled(existingHousehold, existingBase)
    : incomingHousehold && scaled(incomingHousehold, incomingBase);

  const base = { ...existingBase, amount: totalAmount };

  return household ? [household, base] : [base];
};

// Moves the dish ingredient's fiber (`macro.dietaryFiberGrams`) to where the tracker keeps it and
// drops a null `productId` (the GraphQL field is nullable) so only real links reach the form.
const toTrackerIngredient = ({ macro, productId, ...ingredient }: DishIngredient): Ingredient => {
  const base = { ...ingredient, productId: productId ?? undefined };
  if (!macro) return base;

  const { dietaryFiberGrams, ...macroWithoutFiber } = macro;
  return {
    ...base,
    macro: { ...macroWithoutFiber, fiber: { grams: dietaryFiberGrams ?? 0 } },
  };
};

// Helper function to sum ingredients with the same name
const sumIngredients = (ingredients: Ingredient[], portions: number): Ingredient[] => {
  const ingredientMap = new Map<string, Ingredient>();

  ingredients.forEach((ingredient) => {
    if (ingredientMap.has(ingredient.name)) {
      // If ingredient already exists, combine measurements
      const existing = ingredientMap.get(ingredient.name);
      if (!existing) return;

      ingredientMap.set(ingredient.name, {
        ...existing,
        measurements: mergeMeasurements(existing.measurements || [], ingredient.measurements || []),
        kcal: existing.kcal + ingredient.kcal,
        macro:
          existing.macro && ingredient.macro
            ? {
                protein: {
                  grams: existing.macro.protein.grams + ingredient.macro.protein.grams,
                },
                fat: {
                  grams: existing.macro.fat.grams + ingredient.macro.fat.grams,
                },
                carbohydrates: {
                  grams: existing.macro.carbohydrates.grams + ingredient.macro.carbohydrates.grams,
                },
                fiber: {
                  grams: (existing.macro.fiber?.grams ?? 0) + (ingredient.macro.fiber?.grams ?? 0),
                },
              }
            : existing.macro ?? ingredient.macro,
      });
    } else {
      // Add new ingredient
      ingredientMap.set(ingredient.name, {
        ...ingredient,
        measurements: ingredient.measurements ? [...ingredient.measurements] : [],
      });
    }
  });

  return Array.from(ingredientMap.values()).map((ingredient, index) => ({
    ...ingredient,
    id: `ingredient_${index + 1}`,
    measurements: ingredient.measurements?.map((measurement) => ({
      ...measurement,
      amount: measurement.amount / portions,
    })),
    kcal: ingredient.kcal / portions,
    macro: scaleIngredientMacro(ingredient.macro, 1 / portions),
  }));
};

// Helper functions to normalize different data structures
export const normalizeDietMeal = (props: DietMealProps): TrackerAddMealProps => {
  // Extract all ingredients from all dishes
  const allIngredients = props.dishes.flatMap((dish) => dish.ingredients.map(toTrackerIngredient));

  // Sum ingredients with the same name
  const summedIngredients = sumIngredients(allIngredients, 1); // 1 is number of portions

  return {
    mealData: {
      id: props.id,
      name: props.name,
      kcal: props.kcal,
      macro: props.macro,
      preparationImageUrl: props.preparationImageUrl,
      mealType: props.mealType?.type,
      sourceType: 'diet',
      ingredients: summedIngredients,
      portions: 1,
      isFromBaseMeal: false,
    },
    loading: props.loading,
    addMeal: props.addMeal as TrackerAddMealProps['addMeal'],
    saveAsUserMeal: props.saveAsUserMeal,
  };
};

export const normalizeCookbookMeal = (props: CookbookMealProps): TrackerAddMealProps => {
  // Extract all ingredients from all dishes
  const allIngredients = props.dishes.flatMap((dish) => dish.ingredients.map(toTrackerIngredient));

  // Sum ingredients with the same name
  const summedIngredients = sumIngredients(allIngredients, props.portions);

  return {
    mealData: {
      id: props.id,
      name: props.name,
      kcal: props.nutrition.singleServing.kcal,
      macro: {
        protein: {
          grams: props.nutrition.singleServing.protein.grams,
        },
        fat: {
          grams: props.nutrition.singleServing.fat.grams,
        },
        carbohydrates: {
          grams: props.nutrition.singleServing.carbohydrates.grams,
        },
      },
      preparationImageUrl: props.preparationImageUrl,
      sourceType: 'cookbook',
      ingredients: summedIngredients,
      portions: 1,
      isFromBaseMeal: false,
    },
    loading: props.loading,
    addMeal: props.addMeal as TrackerAddMealProps['addMeal'],
    saveAsUserMeal: props.saveAsUserMeal,
  };
};

export const normalizeFavMeal = (props: FavMealProps): TrackerAddMealProps => {
  // Extract all ingredients from all dishes
  const allIngredients = props.dishes.flatMap((dish) => dish.ingredients.map(toTrackerIngredient));

  // Sum ingredients with the same name
  const summedIngredients = sumIngredients(allIngredients, 1); // 1 is number of portions

  return {
    mealData: {
      id: props.id,
      name: props.publicName,
      kcal: props.kcal,
      macro: props.macro,
      preparationImageUrl: props.preparationImageUrl,
      mealType: props.mealType,
      sourceType: 'diet',
      ingredients: summedIngredients,
      portions: 1,
      isFromBaseMeal: false,
    },
    loading: props.loading,
    addMeal: props.addMeal as TrackerAddMealProps['addMeal'],
    saveAsUserMeal: props.saveAsUserMeal,
  };
};

export const normalizeTrackerMeal = (props: TrackerMealProps): TrackerAddMealProps => {
  const ingredients: Ingredient[] = (props.trackerMealData.products ?? []).map((product) => ({
    id: `product_${product.id}`,
    productId: product.productId ?? undefined,
    name: product.name,
    brand: product.brand,
    kcal: product.calories,
    isUserProduct: Boolean(product.isUserProduct),
    measurements: product.measurement
      ? [
          {
            measureId: product.measurement.measureId,
            amount: product.measurement.amount,
            text: product.measurement.text,
            unit: product.measurement.unit,
          },
          {
            amount: product.baseAmount,
            text: `${product.baseAmount} ${product.baseUnit}`,
            unit: product.baseUnit,
          },
        ]
      : [
          {
            amount: product.baseAmount,
            text: `${product.baseAmount} ${product.baseUnit}`,
            unit: product.baseUnit,
          },
        ],
    macro: {
      protein: { grams: product.proteinGrams },
      fat: { grams: product.fatGrams },
      carbohydrates: { grams: product.carbsGrams },
      fiber: { grams: product.fiberGrams ?? 0 },
    },
  }));

  return {
    mealData: {
      id: props.trackerMealData.id,
      name: props.trackerMealData.name,
      kcal: props.trackerMealData.calories,
      macro: {
        protein: { grams: props.trackerMealData.proteinGrams },
        fat: { grams: props.trackerMealData.fatGrams },
        carbohydrates: { grams: props.trackerMealData.carbsGrams },
      },
      preparationImageUrl: props.trackerMealData.imageUrl,
      sourceType: props.trackerMealData.sourceType as UnifiedMealData['sourceType'],
      ingredients,
      portions: props.trackerMealData.portions,
      loggedAt: props.trackerMealData.loggedAt,
      notes: props.trackerMealData.notes || '',
      isFromBaseMeal: props.trackerMealData.isFromBaseMeal,
    },
    loading: props.loading,
    addMeal: props.addMeal as TrackerAddMealProps['addMeal'],
    addUserMeal: props.addUserMeal,
    editMeal: props.editMeal,
    removeMeal: props.removeMeal,
    saveAsUserMeal: props.saveAsUserMeal,
    date: props.date,
    dietMealId: props.dietMealId,
  };
};

export const normalizeDescribeMeal = (props: DescribeMealProps): TrackerAddMealProps => {
  const sourceIngredients = props.ingredients ?? [];
  const ingredients: Ingredient[] = sourceIngredients.map((ingredient, index) => ({
    id: `ingredient_${index + 1}`,
    productId: ingredient.productId ?? undefined,
    name: ingredient.name,
    brand: ingredient.brand,
    kcal: ingredient.calories,
    isUserProduct: Boolean(ingredient.isUserProduct),
    measurements: ingredient.measurement
      ? [
          {
            measureId: ingredient.measurement.measureId,
            amount: ingredient.measurement.amount,
            text: ingredient.measurement.text,
            unit: ingredient.measurement.unit,
          },
          {
            amount: ingredient.baseAmount,
            text: `${ingredient.baseAmount} ${ingredient.baseUnit}`,
            unit: ingredient.baseUnit,
          },
        ]
      : [
          {
            amount: ingredient.baseAmount,
            text: `${ingredient.baseAmount} ${ingredient.baseUnit}`,
            unit: ingredient.baseUnit,
          },
        ],
    macro: {
      protein: { grams: ingredient.proteinGrams },
      fat: { grams: ingredient.fatGrams },
      carbohydrates: { grams: ingredient.carbsGrams },
      fiber: { grams: ingredient.fiberGrams ?? 0 },
    },
  }));

  // Calculate total nutrition values
  const totalKcal = ingredients.reduce((sum, ing) => sum + ing.kcal, 0);
  const totalProtein = ingredients.reduce((sum, ing) => sum + (ing.macro?.protein.grams || 0), 0);
  const totalFat = ingredients.reduce((sum, ing) => sum + (ing.macro?.fat.grams || 0), 0);
  const totalCarbs = ingredients.reduce(
    (sum, ing) => sum + (ing.macro?.carbohydrates.grams || 0),
    0
  );

  return {
    mealData: {
      id: props.dietMealId,
      name: props.mealName,
      kcal: totalKcal,
      macro: {
        protein: { grams: totalProtein },
        fat: { grams: totalFat },
        carbohydrates: { grams: totalCarbs },
      },
      preparationImageUrl: props.imageUrl,
      sourceType: 'ai_description',
      // Scan plate and describe meal both land here, so the caller says which one it is.
      aiDescriptionSource: props.aiDescriptionSource,
      ingredients,
      portions: props.portions,
      isFromBaseMeal: false,
    },
    addMeal: props.addMeal as TrackerAddMealProps['addMeal'],
    saveAsUserMeal: props.saveAsUserMeal,
  };
};

export const normalizeBarcodeMeal = (props: DescribeMealProps): TrackerAddMealProps => {
  const ingredients: Ingredient[] = props.ingredients.map((ingredient, index) => {
    // When the product exposes a "portion" measure whose weight unit matches the
    // product's base dimension, default to one portion: use the portion's grams
    // as the mass and scale nutrition from the per-baseAmount response.
    const portionMeasure = (ingredient.measures ?? []).find(
      (m) => m.unit === 'portion' && m.amount?.value > 0 && m.amount?.unit === ingredient.baseUnit
    );

    const scale =
      portionMeasure && ingredient.baseAmount > 0
        ? portionMeasure.amount.value / ingredient.baseAmount
        : 1;

    const baseAmount = portionMeasure ? portionMeasure.amount.value : ingredient.baseAmount;

    const baseMeasurement = {
      amount: baseAmount,
      text: `${baseAmount} ${ingredient.baseUnit}`,
      unit: ingredient.baseUnit,
    };

    const measurements = portionMeasure
      ? [
          {
            measureId: portionMeasure.measureId,
            amount: 1,
            text: '1 portion',
            unit: 'portion',
          },
          baseMeasurement,
        ]
      : [baseMeasurement];

    return {
      id: `ingredient_${index + 1}`,
      productId: ingredient.productId ?? undefined,
      name: ingredient.name,
      brand: ingredient.brand,
      kcal: ingredient.calories * scale,
      isUserProduct: Boolean(ingredient.isUserProduct),
      measurements,
      macro: {
        protein: { grams: ingredient.proteinGrams * scale },
        fat: { grams: ingredient.fatGrams * scale },
        carbohydrates: { grams: ingredient.carbsGrams * scale },
        fiber: { grams: (ingredient.fiberGrams ?? 0) * scale },
      },
    };
  });

  // Calculate total nutrition values
  const totalKcal = ingredients.reduce((sum, ing) => sum + ing.kcal, 0);
  const totalProtein = ingredients.reduce((sum, ing) => sum + (ing.macro?.protein.grams || 0), 0);
  const totalFat = ingredients.reduce((sum, ing) => sum + (ing.macro?.fat.grams || 0), 0);
  const totalCarbs = ingredients.reduce(
    (sum, ing) => sum + (ing.macro?.carbohydrates.grams || 0),
    0
  );

  return {
    mealData: {
      id: props.dietMealId,
      name: props.mealName,
      kcal: totalKcal,
      macro: {
        protein: { grams: totalProtein },
        fat: { grams: totalFat },
        carbohydrates: { grams: totalCarbs },
      },
      preparationImageUrl: props.imageUrl,
      sourceType: 'ai_description',
      // Only the barcode scanner reaches this normalizer, so the source is fixed here rather than
      // asked of the caller.
      aiDescriptionSource: 'fetch_product_by_ean',
      ingredients,
      portions: props.portions,
      isFromBaseMeal: false,
    },
    addMeal: props.addMeal as TrackerAddMealProps['addMeal'],
  };
};
